import React, { MutableRefObject, useEffect, useRef } from 'react';
import { WizardArtBoard } from '../art-board';
import { WizardContentWelcome } from './welcome';
import { WizardContentSectionAddLinks } from './section/add-links';
import { FileWithMeta, WizardContentSectionUploadFile } from './section/upload-file';
import { WizardContentSectionLibrary } from './section/library';
import isNull from 'lodash/isNull';
import delay from 'lodash/delay';
import { Nullable } from 'src/types/nullable.type';
import { WizardContentListContainer } from './list/container';
import { useDashboardLibraryContentLinkStore } from 'src/dashboard/library/content/link-store';
import { apiCreateOrUpdateNode, apiGetEmbedDetailsFromUrl } from 'src/utils/journeyApi';
import { useCurrentOrganization } from 'src/store/organization';
import { useCurrentUser } from 'src/store/user';
import { WizardModal, WizardModalProps } from '../modal';
import { Node } from 'src/common/interfaces/node.interface';
import { useWizardStore, WIZARD_INITIAL_PLACEHOLDER_CARDS } from '../store';
import { useNewJourneyStore } from 'src/dashboard/store/new-journey';
import { racePromiseWithTimeout } from 'src/common/helpers/race-promise-with-timeout';
import { useNotificationMessage } from 'src/common/notification/message.hook';
import { functionNoop } from 'src/utils/function/noop';
import { getFormParamsByNode } from 'src/dashboard/library/content/helpers/get-form-params-by-node';
import { useFilesUploadStore } from 'src/common/store/files-upload.store';
import { ContentTypeEnum } from 'src/common/interfaces/content/type.enum';
import { shallow } from 'zustand/shallow';
import { useUserOnboardingStore } from 'src/onboarding/store/user-onboarding.store';

export type WizardContentCardsType = WizardContentCardType[];

interface WizardDialogProps extends Pick<WizardModalProps, 'onClose'> {
  openRef: MutableRefObject<() => void>;
  closeRef: MutableRefObject<() => void>;
  onCreateJourneyClick: () => void;
}

type WizardContentStack = {
  type: 'existing_content' | 'file' | 'url';
  content: Node | File;
  meta?: string;
};

export const CREATING_NODE_MAX_DURATION = 3000;

export type WizardContentCardType = Nullable<WizardContentStack> | undefined;

export const WizardContentScreen = ({
  openRef,
  closeRef,
  onCreateJourneyClick,
  onClose = functionNoop,
}: WizardDialogProps) => {
  const { setErrorNotification } = useNotificationMessage();
  const { currentOrganization, getMemberWorkspaces } = useCurrentOrganization();
  const { currentUser } = useCurrentUser();
  const { lastCancelledFileName, removeCancelledFileName, hasOngoingUploadCall } = useFilesUploadStore(
    (state) => ({
      lastCancelledFileName: state.lastCancelledFileName,
      removeCancelledFileName: state.removeLastCancelledFileName,
      hasOngoingUploadCall: state.getOngoingUploadStacks().length !== 0,
    }),
    shallow
  );
  const setOnboardingOpen = useUserOnboardingStore((state) => state.setOpen);
  const isErrorNotificationShownRef = useRef(false);
  const isWizardCompleted = useWizardStore((state) => state.isWizardCompleted);
  const { journeyWorkspace, resetNewJourneyStore } = useNewJourneyStore((state) => ({
    journeyWorkspace: state.journeyWorkspace,
    resetNewJourneyStore: state.resetStore,
  }));
  const memberWorkspaces = getMemberWorkspaces(currentUser);
  const [publicWorkspace] = memberWorkspaces.filter(({ workspace_type }) => workspace_type === 'public');
  const {
    contentCards,
    setContentCards,
    setWorkspaceAndOrganization,
    removeNodeCreationFromStackByIndex,
    stackNodeCreation,
  } = useWizardStore((state) => ({
    contentCards: state.contentCards,
    setContentCards: state.setContentCards,
    stackNodeCreation: state.stackNodeCreation,
    removeNodeCreationFromStackByIndex: state.removeNodeCreationFromStackByIndex,
    setWorkspaceAndOrganization: state.setWorkspaceAndOrganization,
  }));

  const lastElementRef = useRef<Nullable<HTMLDivElement>>(null);
  const { setLoading: setContentLinkStoreLoading, resetStore: resetContentLinkStore } =
    useDashboardLibraryContentLinkStore();

  useEffect(() => {
    if (isWizardCompleted) {
      setContentCards([...WIZARD_INITIAL_PLACEHOLDER_CARDS]);
    }
  }, [isWizardCompleted]);

  useEffect(() => {
    if (lastCancelledFileName) {
      const indexToRemove = contentCards.findIndex((card) => card?.content.name === lastCancelledFileName);
      onContentDeleted(indexToRemove);
      removeCancelledFileName();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastCancelledFileName]);

  useEffect(() => {
    if (publicWorkspace && currentOrganization) {
      setWorkspaceAndOrganization(currentOrganization, journeyWorkspace || publicWorkspace);
    }
  }, [publicWorkspace, journeyWorkspace, currentOrganization, setWorkspaceAndOrganization]);

  const updateListPlacement = (stack: WizardContentStack) => {
    const indexToPlace = contentCards.findIndex((card) => isNull(card));
    if (indexToPlace === -1) {
      contentCards.push(stack);
    } else {
      contentCards[indexToPlace] = stack;
    }
  };

  const scrollListBottom = () => {
    lastElementRef.current!.scrollIntoView({ behavior: 'smooth' });
  };

  const createUrlContent = async (name: string, url: string) => {
    const isURLTargetToMail = url.includes('@');
    let contentType = ContentTypeEnum.EXTERNAL_LINK;
    let contentTypeParam = 'link_url';
    let contentName = name;
    const urlToAdd = isURLTargetToMail && !url.includes('mailto:') ? `mailto:${url}` : url;
    if (!isURLTargetToMail) {
      try {
        const response = await apiGetEmbedDetailsFromUrl(urlToAdd);
        if (response.meta_data) {
          contentName = response.meta_data.title;
        }
        if ((response.transformed_url && response.iframe_safe) || response.third_party_iframe_safe) {
          contentType = ContentTypeEnum.EMBED;
          contentTypeParam = 'url';
        }
      } catch (err) {
        console.error(err);
      }
    }
    return racePromiseWithTimeout(
      apiCreateOrUpdateNode(currentOrganization.id, publicWorkspace.id, contentType, null, {
        name: contentName,
        [contentTypeParam]: urlToAdd,
      }).catch((err) => {
        if (!isErrorNotificationShownRef.current) {
          isErrorNotificationShownRef.current = true;
          setErrorNotification('Something went wrong.');
        } else {
          isErrorNotificationShownRef.current = false;
        }
      }),
      CREATING_NODE_MAX_DURATION
    ).catch((err) => {
      if (!isErrorNotificationShownRef.current) {
        isErrorNotificationShownRef.current = true;
        setErrorNotification('Something went wrong.');
      } else {
        isErrorNotificationShownRef.current = false;
      }
    });
  };

  const onLinksAdded = (links: string[]) => {
    setContentLinkStoreLoading(true);
    Promise.all(links.map((link) => createUrlContent(link, link))).then((nodes: Node[]) => {
      const addedStacks: WizardContentStack[] = nodes
        .filter((node) => typeof node !== 'undefined')
        .map((node) => ({ type: 'url', content: node }));
      addedStacks.forEach(updateListPlacement);
      setContentCards([...contentCards]);
      nodes
        .filter((node) => typeof node !== 'undefined')
        .forEach((node) => {
          stackNodeCreation(node.content_type, getFormParamsByNode(node));
        });
      resetContentLinkStore();
      scrollListBottom();
    });
  };

  const onLibraryContentPicked = async (node: Node) => {
    const addedStack: WizardContentStack = { type: 'existing_content', content: node };
    updateListPlacement(addedStack);
    setContentCards([...contentCards]);
    stackNodeCreation(node.content_type, getFormParamsByNode(node));
    delay(scrollListBottom, 200);
  };

  const onFilesSelected = (files: FileWithMeta[]) => {
    const addedStacks: WizardContentStack[] = files.map(({ file, meta }) => ({ type: 'file', content: file, meta }));
    addedStacks.forEach(updateListPlacement);
    setContentCards([...contentCards]);
    scrollListBottom();
  };

  const onContentDeleted = (indexToRemove: number) => {
    contentCards[indexToRemove] = undefined;
    setContentCards([...contentCards].filter((card) => typeof card !== 'undefined'));
    removeNodeCreationFromStackByIndex(indexToRemove);
  };

  const hideBuildCard = contentCards.every(isNull);

  return (
    <WizardModal
      onClose={() => {
        if (!isNull(journeyWorkspace)) {
          resetNewJourneyStore();
        }
        onClose();
      }}
      onOpen={() => {
        if (hasOngoingUploadCall) {
          setOnboardingOpen(true);
        }
      }}
      openRef={openRef}
      closeRef={closeRef}
    >
      <div className='w-full mt-10 md:ml-40 mx-4'>
        <div className='flex gap-20 w-full md:w-[calc(100%-352px)] h-full pb-2'>
          <div className='flex flex-col mt-10 gap-4 w-[336px]'>
            <WizardContentWelcome />
            <div className='flex flex-col mt-4'>
              <WizardContentSectionAddLinks onAddLinks={onLinksAdded} onLinkNotFound={resetContentLinkStore} />
              <WizardContentSectionUploadFile onSelectFiles={onFilesSelected} />
              <WizardContentSectionLibrary onSelectContent={onLibraryContentPicked} />
            </div>
          </div>
          <div className='flex w-[384px] shrink-0'>
            <WizardContentListContainer
              showBuildCard={!hideBuildCard}
              lastElementRef={lastElementRef}
              onDeleteContent={onContentDeleted}
              cards={contentCards}
              onCreateJourneyClick={onCreateJourneyClick}
            />
          </div>
        </div>
        <WizardArtBoard hide={!hideBuildCard} />
      </div>
    </WizardModal>
  );
};
